// UC20-11 — inactivate or activate a medicine (the position) from where it is listed or
// opened, the way a single code already can be. TC-UC20-11-02 steps 2-3; BUG-1-083.
// per docs/shape-uc-20-medicine-nomenclator.md §5
//
// Inactivating a position inactivates its codes with it (the catalogue cascades; UC50-01
// forbids a new lot on either), so the confirmation says so before it happens. Activating
// wakes the codes the position put to sleep and leaves the ones an administrator retired
// by hand, so there is nothing to confirm there.
import { useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { useToast } from '@simsm/ui-shared';
import { apiErrorKey } from '../../app/api-client';
import { useConfirm } from '../../components/ConfirmDialog/ConfirmDialog';
import { setMedicineActive } from './medicines-api';

export function usePositionActivation(): {
  toggle: (position: { id: string; active: boolean; name: string }) => Promise<void>;
  isPending: boolean;
} {
  const { t } = useTranslate();
  const confirm = useConfirm();
  const toast = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean; name: string }) =>
      setMedicineActive(id, active),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['admin-medicines'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-medicine-code'] });
      toast({
        variant: variables.active ? 'success' : 'info',
        title: variables.active ? t('medicines.toast.activated') : t('medicines.toast.deactivated'),
        text: variables.name,
      });
    },
    // A refused change must not look like an accepted one.
    onError: (err, variables) =>
      toast({
        variant: 'error',
        title: t(
          apiErrorKey(
            err,
            variables.active
              ? 'medicines.error.activate_failed'
              : 'medicines.error.deactivate_failed',
          ),
        ),
        text: variables.name,
      }),
  });

  const { mutate } = mutation;
  const toggle = useCallback(
    async ({ id, active, name }: { id: string; active: boolean; name: string }): Promise<void> => {
      // `active` is the position's state now; the write is its opposite.
      if (active) {
        const ok = await confirm({
          title: t('medicines.confirm.deactivate.title'),
          body: t('medicines.confirm.deactivate.body', { name }),
          confirmLabel: t('confirm.action.deactivate'),
          destructive: true,
        });
        if (!ok) return;
      }
      mutate({ id, active: !active, name });
    },
    [confirm, mutate, t],
  );

  return { toggle, isPending: mutation.isPending };
}
